/**
 * AIMDv2 – AudienceDropdown
 *
 * Custom grouped multi-select dropdown for Program Audience.
 * Items are grouped by their `category` field (e.g. "Departments", "Groups").
 * Each group has a header label ("Attributes: <category>").
 * A "Select All" option at the top toggles every item on/off.
 *
 * Selected items render as chips with the format "<Category> (<Label>)".
 * The dropdown panel uses a Popper + ClickAwayListener (not MUI Select)
 * to allow full styling control matching the Figma spec.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Popper,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AudienceOption } from '../types';
import { MAX_AUDIENCE_SELECTIONS } from '../constants';

interface AudienceDropdownProps {
  label?: string;
  options: AudienceOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  /** Shows a spinner inside the trigger while options are being fetched. */
  loading?: boolean;
  /** Cap on how many options can be selected at once. */
  maxSelections?: number;
}

/** Group options by their category field, preserving insertion order. */
function groupByCategory(
  options: AudienceOption[]
): { category: string; items: AudienceOption[] }[] {
  const map = new Map<string, AudienceOption[]>();
  for (const opt of options) {
    const cat = opt.category || 'Other';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(opt);
  }
  return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
}

const AudienceDropdown: React.FC<AudienceDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder = 'Select audience...',
  disabled = false,
  error,
  helperText,
  loading = false,
  maxSelections = MAX_AUDIENCE_SELECTIONS
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const groups = useMemo(() => groupByCategory(options), [options]);
  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const allSelected = allValues.length > 0 && allValues.every((v) => value.includes(v));

  const handleToggle = () => {
    if (!disabled && !loading) setOpen((prev) => !prev);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(allValues.slice(0, maxSelections));
    }
  };

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else if (value.length < maxSelections) {
      onChange([...value, optionValue]);
    }
  };

  const handleRemoveChip = (optionValue: string) => {
    onChange(value.filter((v) => v !== optionValue));
  };

  /** Build the chip label: "Category (Label)" */
  const getChipLabel = (optValue: string): string => {
    const opt = options.find((o) => o.value === optValue);
    if (!opt) return optValue;
    return opt.category ? `${opt.category} (${opt.label})` : opt.label;
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {/* Label */}
      {label && (
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 500,
            fontFamily: 'Inter',
            color: 'var(--Text-text-primary, #FDFDFD)',
            lineHeight: '20px'
          }}
        >
          {label}
        </Typography>
      )}

      {/* Trigger */}
      <Box
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          width: '100%',
          px: '12px',
          py: '8px',
          background: 'rgba(20,20,20,1)',
          borderRadius: '6px',
          outline: open
            ? '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))'
            : '1px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.4))',
          display: 'flex',
          alignItems: 'center',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          minHeight: 40
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CircularProgress size={14} sx={{ color: 'var(--Text-text-tertiary, #868686)' }} />
              <Typography sx={{ color: 'var(--Text-text-tertiary, #868686)', fontSize: 14, fontFamily: 'Inter' }}>
                Loading options...
              </Typography>
            </Box>
          )}
          {!loading && value.length === 0 && (
            <Typography
              sx={{
                color: 'var(--Text-text-tertiary, #868686)',
                fontSize: 14,
                fontFamily: 'Inter'
              }}
            >
              {placeholder}
            </Typography>
          )}
          {value.map((v) => (
            <Chip
              key={v}
              label={getChipLabel(v)}
              onMouseDown={(e) => e.stopPropagation()}
              onDelete={() => handleRemoveChip(v)}
              deleteIcon={<CloseIcon sx={{ fontSize: 10 }} />}
              sx={{
                height: 24,
                borderRadius: '20px',
                background: 'var(--Fill-Button-button-white, #FDFDFD)',
                color: 'var(--Stroke-solid-black, #0F0F0F)',
                fontSize: 12,
                fontFamily: 'Inter',
                outline: '1px solid var(--Stroke-Border-border, #D4D4D4)',
                outlineOffset: '-1px',
                '& .MuiChip-label': { px: 1, lineHeight: '12px' },
                '& .MuiChip-deleteIcon': {
                  color: 'var(--Stroke-solid-black, #0F0F0F)',
                  fontSize: 10,
                  marginRight: '4px'
                }
              }}
            />
          ))}
        </Box>

        {/* Arrow icon */}
        {open ? (
          <KeyboardArrowUpIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ color: 'var(--Stroke-icon-dim, #868686)', fontSize: 20 }} />
        )}
      </Box>

      {/* Dropdown panel */}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              mt: '4px',
              background: 'rgba(20,20,20,1)',
              borderRadius: '6px',
              outline: '1px solid var(--Stroke-Border-border-highlight, rgba(255,255,255,0.35))',
              overflow: 'hidden',
              maxHeight: 350,
              overflowY: 'auto'
            }}
          >
            {/* Select All */}
            <Box
              onClick={handleSelectAll}
              sx={{
                px: '12px',
                py: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                '&:hover': { background: 'rgba(255,255,255,0.06)' }
              }}
            >
              <Checkbox checked={allSelected} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontFamily: 'Inter',
                  lineHeight: '20px',
                  color: 'var(--Text-text-secondary, #B6B6B6)'
                }}
              >
                Select All
              </Typography>
            </Box>

            {/* Grouped items */}
            {groups.map((group) => (
              <Box key={group.category}>
                {/* Group header */}
                <Box sx={{ px: '12px', pt: '5px', pb: '2px' }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontFamily: 'Inter',
                      fontStyle: 'italic',
                      lineHeight: '16px',
                      color: 'var(--Text-text-tertiary, #868686)'
                    }}
                  >
                    Attributes: {group.category}
                  </Typography>
                </Box>

                {/* Group items */}
                {group.items.map((opt) => {
                  const isSelected = value.includes(opt.value);
                  const isDisabledItem = !isSelected && value.length >= maxSelections;
                  return (
                    <Box
                      key={opt.value}
                      onClick={() => !isDisabledItem && handleSelect(opt.value)}
                      sx={{
                        px: '12px',
                        py: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        cursor: isDisabledItem ? 'not-allowed' : 'pointer',
                        opacity: isDisabledItem ? 0.4 : 1,
                        '&:hover': {
                          background: isDisabledItem ? 'transparent' : 'rgba(255,255,255,0.06)'
                        }
                      }}
                    >
                      <Checkbox checked={isSelected} />
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontFamily: 'Inter',
                          lineHeight: '20px',
                          color: isSelected
                            ? 'var(--Text-text-primary, #FDFDFD)'
                            : 'var(--Text-text-secondary, #B6B6B6)'
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>

      {/* Helper text */}
      {helperText && !error && (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: 'var(--Text-text-secondary, #B6B6B6)',
            lineHeight: '16px'
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Error text */}
      {error && (
        <Typography
          sx={{
            fontSize: 12,
            fontFamily: 'Inter',
            color: 'var(--UI-Error, #F44336)',
            lineHeight: '16px'
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Checkbox – small internal component matching the Figma design token styling
// ---------------------------------------------------------------------------

const Checkbox: React.FC<{ checked: boolean }> = ({ checked }) => (
  <Box
    sx={{
      width: 20,
      height: 20,
      p: '3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}
  >
    <Box
      sx={{
        width: 16,
        height: 16,
        borderRadius: '3px',
        background: 'rgba(0,0,0,0.90)',
        outline: checked
          ? '2px solid var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.7))'
          : '2px solid var(--Stroke-Form-Input-border-light, rgba(134,134,134,0.5))',
        outlineOffset: '-1px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {checked && (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '2px',
            background: 'var(--Stroke-Form-Input-border-highlight, rgba(255,255,255,0.7))'
          }}
        />
      )}
    </Box>
  </Box>
);

export default AudienceDropdown;
